import React, { useMemo, useState } from 'react';
import { Button, Col, Form, Nav, Row, Tab, Table } from 'react-bootstrap';
import dw from './daywiseItinerary.module.css';

const MODES = [
  'Cab · Hatchback',
  'Cab · Sedan',
  'Cab · SUV',
  'Cab · Tempo Traveller',
  'Bus',
  'Train',
  'Flight',
  'Charter',
  'Helicopter',
];

const emptyTransport = (overrides) => ({
  mode: 'Cab · Sedan',
  chargeBasis: 'vehicle',
  capacity: 4,
  rateShared: 0,
  rateIndian: 0,
  rateForeign: 0,
  nationalityPricing: 'shared',
  enabled: true,
  ...overrides,
});

const TransportRow = ({ item, index, list, onChange, onRemove }) => {
  const patch = (patchObj) => {
    const next = [...list];
    next[index] = { ...next[index], ...patchObj };
    onChange(next);
  };

  return (
    <tr>
      <td>
        <Form.Control
          as="select"
          size="sm"
          value={item.mode || ''}
          onChange={(e) => patch({ mode: e.target.value })}
        >
          {MODES.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Form.Control>
      </td>
      <td>
        <Form.Control
          as="select"
          size="sm"
          value={item.chargeBasis || 'vehicle'}
          onChange={(e) => patch({ chargeBasis: e.target.value })}
        >
          <option value="vehicle">Per vehicle</option>
          <option value="person">Per person</option>
        </Form.Control>
      </td>
      <td>
        <Form.Control
          size="sm"
          type="number"
          value={item.capacity ?? 4}
          onChange={(e) => patch({ capacity: Number(e.target.value) })}
        />
      </td>
      <td>
        <Form.Control
          as="select"
          size="sm"
          value={item.nationalityPricing || 'shared'}
          onChange={(e) => patch({ nationalityPricing: e.target.value })}
        >
          <option value="shared">Shared</option>
          <option value="separate">Separate</option>
        </Form.Control>
      </td>
      <td>
        <Form.Control
          size="sm"
          type="number"
          value={item.rateShared ?? 0}
          onChange={(e) => {
            const v = Number(e.target.value);
            patch({ rateShared: v, rateIndian: v });
          }}
        />
      </td>
      <td>
        <Form.Control
          size="sm"
          type="number"
          value={item.rateForeign ?? 0}
          onChange={(e) => patch({ rateForeign: Number(e.target.value) })}
        />
      </td>
      <td>
        <Form.Check
          checked={item.enabled !== false}
          onChange={(e) => patch({ enabled: e.target.checked })}
        />
      </td>
      <td>
        <Button size="sm" variant="outline-danger" onClick={() => onRemove(index)}>
          Remove
        </Button>
      </td>
    </tr>
  );
};

const TransportTable = ({ items, indices, list, onChange }) => {
  const remove = (globalIndex) => {
    const next = list.filter((_, i) => i !== globalIndex);
    onChange(next);
  };

  return (
    <div className={dw.tablewrap}>
      <Table bordered size="sm" className="mb-0">
        <thead>
          <tr>
            <th>Mode</th>
            <th>Basis</th>
            <th>Cap.</th>
            <th>Nat.</th>
            <th>Rate IN/shared</th>
            <th>Rate FN</th>
            <th>On</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {indices.map((globalIdx) => (
            <TransportRow
              key={globalIdx}
              item={list[globalIdx]}
              index={globalIdx}
              list={list}
              onChange={onChange}
              onRemove={remove}
            />
          ))}
          {indices.length === 0 && (
            <tr>
              <td colSpan={8} className="text-muted text-center">
                None
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

const TransportCatalogue = ({ transports, days, onChange }) => {
  const [subTab, setSubTab] = useState('leg');
  const list = transports || [];

  const legs = useMemo(() => {
    const map = new Map();
    (days || []).forEach((d) => {
      if (d.legId) {
        map.set(d.legId, { legId: d.legId, dayNumber: d.dayNumber, title: d.title });
      }
    });
    return [...map.values()];
  }, [days]);

  const addLeg = (leg) => {
    onChange([
      ...list,
      emptyTransport({
        context: 'leg',
        contextId: leg.legId,
        dayNumber: leg.dayNumber,
      }),
    ]);
  };

  const addLocal = (day) => {
    onChange([
      ...list,
      emptyTransport({
        context: 'local',
        contextId: String(day.dayNumber),
        dayNumber: day.dayNumber,
      }),
    ]);
  };

  const addPackage = () => {
    const id = `pkg${Date.now()}`;
    onChange([
      ...list,
      emptyTransport({
        context: 'package',
        contextId: id,
        dayNumber: 0,
        label: 'Full tour package',
      }),
    ]);
  };

  const indicesFor = (pred) => list.map((t, i) => (pred(t) ? i : -1)).filter((i) => i >= 0);

  return (
    <div className={dw.catalogueRoot}>
      <h4>Transport</h4>
      <Tab.Container activeKey={subTab} onSelect={(k) => setSubTab(k || 'leg')}>
        <Nav variant="tabs" className={dw.transportTabs}>
          <Nav.Item>
            <Nav.Link eventKey="leg">Intercity segments</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="local">Local day vehicles</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="package">Full-tour packages</Nav.Link>
          </Nav.Item>
        </Nav>
        <Tab.Content className="pt-3">
          <Tab.Pane eventKey="leg">
            <p className={`${dw.foot} text-muted`} style={{ marginBottom: 12 }}>
              Legs are defined on itinerary days: each travelling day gets a{' '}
              <code>legId</code> (e.g. leg2 on Day 3). Use{' '}
              <strong>Add intercity segment</strong> in a day&apos;s Travel section, or set{' '}
              <code>legId</code> in imported JSON. Then add vehicle modes and rates below.
            </p>
            {legs.map((leg) => (
              <div key={leg.legId} style={{ marginBottom: 16 }}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <strong>
                    {leg.legId} (Day {leg.dayNumber}: {leg.title || '—'})
                  </strong>
                  <Button size="sm" onClick={() => addLeg(leg)}>
                    Add option
                  </Button>
                </div>
                <TransportTable
                  list={list}
                  onChange={onChange}
                  indices={indicesFor(
                    (t) => t.context === 'leg' && t.contextId === leg.legId
                  )}
                />
              </div>
            ))}
            {legs.length === 0 && (
              <p className="text-muted">
                No intercity legs yet. Open a day in the sidebar and use{' '}
                <strong>Add intercity segment</strong> under Travel &amp; local vehicle.
              </p>
            )}
          </Tab.Pane>
          <Tab.Pane eventKey="local">
            {(days || []).map((day) => (
              <div key={day.dayNumber} style={{ marginBottom: 16 }}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <strong>Day {day.dayNumber}</strong>
                  <Button size="sm" onClick={() => addLocal(day)}>
                    Add vehicle
                  </Button>
                </div>
                <TransportTable
                  list={list}
                  onChange={onChange}
                  indices={indicesFor(
                    (t) =>
                      t.context === 'local' &&
                      t.dayNumber === day.dayNumber &&
                      t.contextId === String(day.dayNumber)
                  )}
                />
              </div>
            ))}
          </Tab.Pane>
          <Tab.Pane eventKey="package">
            <Row className="mb-2">
              <Col>
                <Button size="sm" onClick={addPackage}>
                  Add full-tour package
                </Button>
              </Col>
            </Row>
            <TransportTable
              list={list}
              onChange={onChange}
              indices={indicesFor((t) => t.context === 'package')}
            />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default TransportCatalogue;
